import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, MessageSquare, CheckCircle2 } from 'lucide-react';
import { northernLightsExperiences, seasonalAdventures } from '../mock';

// Encode form data as application/x-www-form-urlencoded for Netlify Forms
const encode = (data) =>
  Object.keys(data)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    .join('&');

// Submit to Netlify Forms (works on Netlify-hosted deploy)
const submitToNetlify = (formName, fields) =>
  fetch('/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: encode({ 'form-name': formName, 'bot-field': '', ...fields }),
  });
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const Contact = () => {
  const [searchParams] = useSearchParams();
  const inquiryId = searchParams.get('inquiry');
  
  const [formType, setFormType] = useState(inquiryId ? 'booking' : 'general');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [generalForm, setGeneralForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    experience: inquiryId || '',
    travelDates: '',
    groupSize: '',
    message: ''
  });

  const allExperiences = [...northernLightsExperiences, ...seasonalAdventures];

  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await submitToNetlify('contact', generalForm);
    } catch (err) {
      console.error('Contact form submission error:', err);
    } finally {
      setIsSubmitted(true);
      setIsLoading(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const selectedExp = allExperiences.find(
      (x) => x.id.toString() === bookingForm.experience
    );
    const payload = {
      ...bookingForm,
      experience: selectedExp
        ? `${selectedExp.title} - ${selectedExp.location}`
        : bookingForm.experience,
    };
    try {
      await submitToNetlify('booking-inquiry', payload);
    } catch (err) {
      console.error('Booking inquiry submission error:', err);
    } finally {
      setIsSubmitted(true);
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white p-8 rounded-lg shadow-lg text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-green-50 rounded-full mb-6 mx-auto">
              <CheckCircle2 className="text-green-600" size={32} />
            </div>
            <h2 className="text-3xl font-light text-gray-900 mb-4">
              Message Received
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Thank you for reaching out. We'll get back to you within 24 hours.
            </p>
            <Button
              onClick={() => {
                setIsSubmitted(false);
                setGeneralForm({ name: '', email: '', subject: '', message: '' });
                setBookingForm({ name: '', email: '', phone: '', experience: '', travelDates: '', groupSize: '', message: '' });
              }}
              className="bg-brand-teal text-white hover:bg-brand-teal-dark"
            >
              Send Another Message
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-80 flex items-center justify-center overflow-hidden">
        <img
          src="https://images.pexels.com/photos/29359531/pexels-photo-29359531.jpeg"
          alt="Mountain landscape"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-6">
            Get in Touch
          </h1>
          <p className="text-xl text-white/90 leading-relaxed">
            Questions about an adventure? Let's talk.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-light text-gray-900 mb-6">
                Let's Start a Conversation
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                Whether you're curious about a specific experience, have questions about photography instruction, or just want to chat about chasing light in extraordinary places — we're here.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-normal text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600">hello@lightchasingadventures.com</p>
                    <p className="text-sm text-gray-500 mt-1">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-normal text-gray-900 mb-1">Based in the UK</h3>
                    <p className="text-gray-600">Leading adventures across the Northern Hemisphere</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-normal text-gray-900 mb-3">
                  Before You Reach Out
                </h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Check our Northern Lights and Seasonal Adventures pages for trip details</li>
                  <li>• Download the free Aurora Readiness Guide for planning tips</li>
                  <li>• Read our Philosophy page to see if our approach resonates</li>
                  <li>• No question is too basic — we're here to help</li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gray-50 p-8 rounded-lg">
              {/* Form Type Toggle */}
              <div className="flex space-x-2 mb-6">
                <button
                  onClick={() => setFormType('general')}
                  data-testid="general-toggle-button"
                  className={`flex-1 py-2 px-4 rounded-lg text-sm transition-colors ${
                    formType === 'general'
                      ? 'bg-brand-teal text-white'
                      : 'bg-white text-brand-muted hover:text-brand-navy'
                  }`}
                >
                  General Inquiry
                </button>
                <button
                  onClick={() => setFormType('booking')}
                  data-testid="booking-toggle-button"
                  className={`flex-1 py-2 px-4 rounded-lg text-sm transition-colors ${
                    formType === 'booking'
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Booking Inquiry
                </button>
              </div>

              {/* General Form */}
              {formType === 'general' && (
                <form
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  onSubmit={handleGeneralSubmit}
                  className="space-y-4"
                  data-testid="contact-general-form"
                >
                  <input type="hidden" name="form-name" value="contact" />
                  <p className="hidden">
                    <label>
                      Don't fill this out: <input name="bot-field" />
                    </label>
                  </p>
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      type="text"
                      value={generalForm.name}
                      onChange={(e) => setGeneralForm({ ...generalForm, name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={generalForm.email}
                      onChange={(e) => setGeneralForm({ ...generalForm, email: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      type="text"
                      value={generalForm.subject}
                      onChange={(e) => setGeneralForm({ ...generalForm, subject: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      rows={6}
                      value={generalForm.message}
                      onChange={(e) => setGeneralForm({ ...generalForm, message: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gray-900 text-white hover:bg-gray-800"
                    data-testid="contact-submit-button"
                  >
                    {isLoading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}

              {/* Booking Form */}
              {formType === 'booking' && (
                <form
                  name="booking-inquiry"
                  method="POST"
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  onSubmit={handleBookingSubmit}
                  className="space-y-4"
                  data-testid="contact-booking-form"
                >
                  <input type="hidden" name="form-name" value="booking-inquiry" />
                  <p className="hidden">
                    <label>
                      Don't fill this out: <input name="bot-field" />
                    </label>
                  </p>
                  <div>
                    <Label htmlFor="booking-name">Name</Label>
                    <Input
                      id="booking-name"
                      type="text"
                      value={bookingForm.name}
                      onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-email">Email</Label>
                    <Input
                      id="booking-email"
                      type="email"
                      value={bookingForm.email}
                      onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone (Optional)</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={bookingForm.phone}
                      onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="experience">Experience</Label>
                    <Select
                      value={bookingForm.experience}
                      onValueChange={(value) => setBookingForm({ ...bookingForm, experience: value })}
                      required
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select an experience" />
                      </SelectTrigger>
                      <SelectContent>
                        {allExperiences.map((exp) => (
                          <SelectItem key={exp.id} value={exp.id.toString()}>
                            {exp.title} - {exp.location}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="dates">Preferred Travel Dates</Label>
                    <Input
                      id="dates"
                      type="text"
                      placeholder="e.g., January 2026 or flexible"
                      value={bookingForm.travelDates}
                      onChange={(e) => setBookingForm({ ...bookingForm, travelDates: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="groupSize">Group Size</Label>
                    <Input
                      id="groupSize"
                      type="text"
                      placeholder="e.g., 2 people"
                      value={bookingForm.groupSize}
                      onChange={(e) => setBookingForm({ ...bookingForm, groupSize: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-message">Additional Information</Label>
                    <Textarea
                      id="booking-message"
                      rows={4}
                      placeholder="Tell us about your photography experience, any questions, or special requirements..."
                      value={bookingForm.message}
                      onChange={(e) => setBookingForm({ ...bookingForm, message: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-brand-teal text-white hover:bg-brand-teal-dark"
                    data-testid="booking-submit-button"
                  >
                    {isLoading ? 'Sending...' : 'Submit Booking Inquiry'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;